// src/pages/Wallet/hooks/usePriceData.js
import { useState, useEffect, useContext, useRef } from 'react';
import { NetworkContext } from '../../../contexts/NetworkContext';
import secureFetch from '../../../utils/secureFetch';

// Cache for storing token prices and reducing API calls
// Structure: { [network_address]: { price: number, imageUrl: string, timestamp: number } }
// Structure: { [network_address_price_change]: { priceChange: number, timestamp: number } }
const tokenPriceCache = {};

// Cache expiration time in milliseconds (1 minute)
const CACHE_EXPIRATION = 1 * 60 * 1000;

// Rate limiting variables
const MAX_CALLS_PER_MINUTE = 30;
const apiCallLog = [];

/**
 * Helper function to check if we can make an API call
 * within rate limits (30 calls per minute)
 */
function canMakeApiCall() {
  const now = Date.now();
  const oneMinuteAgo = now - 60000;
  
  // Remove API calls older than 1 minute
  while (apiCallLog.length > 0 && apiCallLog[0] < oneMinuteAgo) {
    apiCallLog.shift();
  }
  
  // Check if we're under the rate limit
  return apiCallLog.length < MAX_CALLS_PER_MINUTE;
}

/**
 * Helper function to log an API call for rate limiting
 */
function logApiCall() {
  apiCallLog.push(Date.now());
}

/**
 * Helper function to wait until we can make an API call
 */
async function waitForRateLimit() {
  if (canMakeApiCall()) {
    return;
  }
  
  // Calculate how long to wait (in ms)
  const oldestCall = apiCallLog[0];
  const waitTime = 60000 - (Date.now() - oldestCall) + 100; // Add 100ms buffer
  
  console.log(`Rate limit reached. Waiting ${waitTime}ms before making next API call.`);
  
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, waitTime);
  });
}

/**
 * Helper function to fetch multiple token prices in a single batch request
 * Includes caching and rate limiting
 */
async function fetchTokenPricesBatch(network, tokenAddresses) {
  if (!tokenAddresses || tokenAddresses.length === 0) return {};
  
  try {
    // GeckoTerminal has a limit of 30 tokens per batch request
    const BATCH_SIZE = 30;
    const prices = {};
    const logos = {};
    const uncachedAddresses = [];
    const now = Date.now();
    
    // Check cache first for each address
    tokenAddresses.forEach(address => {
      const lowerAddress = address.toLowerCase();
      const cacheKey = `${network}_${lowerAddress}`;
      
      if (tokenPriceCache[cacheKey] && 
          now - tokenPriceCache[cacheKey].timestamp < CACHE_EXPIRATION) {
        // Use cached price if available and not expired
        prices[lowerAddress] = tokenPriceCache[cacheKey].price;
        
        // Also get logo if available
        if (tokenPriceCache[cacheKey].imageUrl) {
          logos[lowerAddress] = tokenPriceCache[cacheKey].imageUrl;
        }
      } else {
        // Add to list of addresses to fetch
        uncachedAddresses.push(address);
      }
    });
    
    // If all prices were in cache, return immediately
    if (uncachedAddresses.length === 0) {
      console.log(`All ${tokenAddresses.length} token prices found in cache`);
      return { prices, logos };
    }
    
    console.log(`Found ${tokenAddresses.length - uncachedAddresses.length} tokens in cache, fetching ${uncachedAddresses.length} tokens`);
    
    // Process tokens in batches of 30
    for (let i = 0; i < uncachedAddresses.length; i += BATCH_SIZE) {
      // Wait if we're hitting rate limits
      await waitForRateLimit();
      
      const batchAddresses = uncachedAddresses.slice(i, i + BATCH_SIZE);
      const commaSeparatedAddresses = batchAddresses.join('%2C');
      
      const url = `https://api.geckoterminal.com/api/v2/networks/${network}/tokens/multi/${commaSeparatedAddresses}`;
      console.log(`Fetching batch ${Math.floor(i/BATCH_SIZE) + 1}: ${batchAddresses.length} tokens`);
      
      // Log this API call for rate limiting
      logApiCall();
      
      // Use secureFetch instead of fetch
      const response = await secureFetch(url);
      const json = await response.json();
      
      if (json?.data && Array.isArray(json.data)) {
        // Process each token in the response
        json.data.forEach(token => {
          if (token?.attributes?.address && token?.attributes?.price_usd) {
            const address = token.attributes.address.toLowerCase();
            const price = parseFloat(token.attributes.price_usd);
            const imageUrl = token.attributes.image_url || null; // Extract image URL
            
            // Update cache
            const cacheKey = `${network}_${address}`;
            tokenPriceCache[cacheKey] = {
              price,
              imageUrl, // Add imageUrl to cache
              timestamp: Date.now()
            };
            
            // Add to result
            prices[address] = price;
            
            // Add logo to result if available
            if (imageUrl) {
              logos[address] = imageUrl;
            }
          }
        });
      }
    }
    
    return { prices, logos };
  } catch (error) {
    console.error("Error fetching token prices in batch:", error);
    return { prices: {}, logos: {} };
  }
}

/**
 * Helper function to fetch price changes for multiple tokens
 * Uses batch requests with caching and rate limiting
 */
async function fetchTokenPriceChanges(network, tokenAddresses) {
  if (!tokenAddresses || tokenAddresses.length === 0) return {};
  
  try {
    // GeckoTerminal has a limit of 30 tokens per batch request
    const BATCH_SIZE = 30;
    const priceChanges = {};
    const uncachedAddresses = [];
    const now = Date.now();
    
    // Check cache first for each address
    tokenAddresses.forEach(address => {
      const lowerAddress = address.toLowerCase();
      const cacheKey = `${network}_${lowerAddress}_price_change`;
      
      if (tokenPriceCache[cacheKey] && 
          now - tokenPriceCache[cacheKey].timestamp < CACHE_EXPIRATION) {
        // Use cached price change if available and not expired
        priceChanges[lowerAddress] = tokenPriceCache[cacheKey].priceChange;
      } else {
        // Add to list of addresses to fetch
        uncachedAddresses.push(address);
      }
    });
    
    // If all price changes were in cache, return immediately
    if (uncachedAddresses.length === 0) {
      console.log(`All ${tokenAddresses.length} token price changes found in cache`);
      return priceChanges;
    }
    
    console.log(`Found ${tokenAddresses.length - uncachedAddresses.length} token price changes in cache, fetching ${uncachedAddresses.length} tokens`);
    
    // Process tokens in batches of 30
    for (let i = 0; i < uncachedAddresses.length; i += BATCH_SIZE) {
      // Wait if we're hitting rate limits
      await waitForRateLimit();
      
      const batchAddresses = uncachedAddresses.slice(i, i + BATCH_SIZE);
      const commaSeparatedAddresses = batchAddresses.join('%2C');
      
      const url = `https://api.geckoterminal.com/api/v2/simple/networks/${network}/token_price/${commaSeparatedAddresses}?include_24hr_price_change=true`;
      console.log(`Fetching price changes batch ${Math.floor(i/BATCH_SIZE) + 1}: ${batchAddresses.length} tokens`);
      
      // Log this API call for rate limiting
      logApiCall();
      
      // Use secureFetch instead of fetch
      const response = await secureFetch(url);
      const json = await response.json();
      
      if (json?.data?.attributes?.h24_price_change_percentage) {
        const h24Changes = json.data.attributes.h24_price_change_percentage;
        
        // Process each token in the response
        Object.entries(h24Changes).forEach(([address, priceChange]) => {
          const lowerAddress = address.toLowerCase();
          
          // Update cache
          const cacheKey = `${network}_${lowerAddress}_price_change`;
          tokenPriceCache[cacheKey] = {
            priceChange: parseFloat(priceChange),
            timestamp: Date.now()
          };
          
          // Add to result
          priceChanges[lowerAddress] = parseFloat(priceChange);
        });
      }
    }
    
    return priceChanges;
  } catch (error) {
    console.error("Error fetching token price changes in batch:", error);
    return {};
  }
}

/**
 * Hook to fetch and manage token price data
 * Uses batch requests with caching and rate limiting
 */
export const usePriceData = (tokens, hiddenTokens = []) => {
  const { currentChain } = useContext(NetworkContext);
  const [nativePrice, setNativePrice] = useState(null);
  const [nativePriceChange, setNativePriceChange] = useState(null); // Add state for native price change
  const [tokenPrices, setTokenPrices] = useState({});
  const [tokenLogos, setTokenLogos] = useState({});
  const [tokenPriceChanges, setTokenPriceChanges] = useState({});
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const fetchTimeoutRef = useRef(null);

  // Fetch native token price
  useEffect(() => {
    async function loadNativePrice() {
      if (!currentChain || !currentChain.coingeckoAPI) {
        setNativePrice(null);
        setNativePriceChange(null);
        return;
      }
      
      // Check cache first
      const cacheKey = `native_${currentChain.key}`;
      const cacheKeyChange = `native_${currentChain.key}_change`;
      const now = Date.now();
      
      if (tokenPriceCache[cacheKey] && 
          tokenPriceCache[cacheKeyChange] &&
          now - tokenPriceCache[cacheKey].timestamp < CACHE_EXPIRATION) {
        setNativePrice(tokenPriceCache[cacheKey].price);
        setNativePriceChange(tokenPriceCache[cacheKeyChange].priceChange);
        return;
      }
      
      try {
        // Wait if we're hitting rate limits
        await waitForRateLimit();
        
        // Log this API call for rate limiting
        logApiCall();
        
        // Use secureFetch instead of fetch
        const resp = await secureFetch(currentChain.coingeckoAPI);
        const data = await resp.json();
        const firstKey = Object.keys(data)[0];
        
        if (data[firstKey]) {
          // Extract price
          const price = data[firstKey].usd;
          setNativePrice(price);
          
          // Update price cache
          tokenPriceCache[cacheKey] = {
            price,
            timestamp: now
          };
          
          // Extract 24h price change
          if (data[firstKey].usd_24h_change !== undefined) {
            const priceChange = data[firstKey].usd_24h_change;
            setNativePriceChange(priceChange);
            
            // Update price change cache
            tokenPriceCache[cacheKeyChange] = {
              priceChange,
              timestamp: now
            };
            
            console.log(`Native token (${firstKey}) 24h change: ${priceChange}%`);
          } else {
            console.log("24h price change not found in API response");
            setNativePriceChange(null);
          }
        } else {
          setNativePrice(null);
          setNativePriceChange(null);
        }
      } catch (err) {
        console.error("Error fetching native token price:", err);
        setNativePrice(null);
        setNativePriceChange(null);
      }
    }
    
    loadNativePrice();
  }, [currentChain]);

  // Fetch ERC-20 token prices and logos in batches with rate limiting
  useEffect(() => {
    // Clear any pending timeout when dependencies change
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    async function fetchAllTokenData() {
      if (!currentChain || tokens.length === 0) return;
      
      // Only fetch if enough time has passed since last refresh
      const now = Date.now();
      if (now - lastRefreshTime < 3000) { // 3 seconds minimum between refreshes
        return;
      }
      
      setLastRefreshTime(now);
      
      // Filter tokens:
      // 1. Only include ERC-20 tokens
      // 2. Exclude hidden tokens
      // 3. Make sure they have valid addresses
      // 4. Only include tokens from the current chain
      const erc20Tokens = tokens.filter(t => 
        t.token && 
        t.token.type === "ERC-20" &&
        t.token.address &&
        !(hiddenTokens && hiddenTokens.includes(t.token.address.toLowerCase())) &&
        (!t.token.chainKey || t.token.chainKey === currentChain.key)
      );
      
      if (erc20Tokens.length === 0) {
        setTokenPrices({});
        setTokenLogos({});
        setTokenPriceChanges({});
        return;
      }
      
      // Get all token addresses for the batch request
      const tokenAddresses = erc20Tokens.map(t => t.token.address);
      
      // Get the appropriate network ID for GeckoTerminal API
      const geckoNetwork = currentChain.geckoTerminalId || currentChain.key;
      
      // Fetch prices and logos in batches of 30 tokens with caching
      const { prices, logos } = await fetchTokenPricesBatch(geckoNetwork, tokenAddresses);
      setTokenPrices(prices);
      setTokenLogos(logos);
      
      // Fetch price changes
      const priceChanges = await fetchTokenPriceChanges(geckoNetwork, tokenAddresses);
      setTokenPriceChanges(priceChanges);
    }
    
    // Start fetching with a small delay to handle rapid changes
    fetchTimeoutRef.current = setTimeout(() => {
      fetchAllTokenData();
    }, 300);
    
    // Clean up timeout on unmount
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [tokens, hiddenTokens, currentChain, lastRefreshTime]);

  return {
    nativePrice,
    nativePriceChange, 
    tokenPrices,
    tokenLogos,
    tokenPriceChanges
  };
};